import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FirebaseContext } from "../contexts/FirebaseContext";
import { useTheme } from "../contexts/ThemeContext";
import TopBar from "../components/TopBar";
import ToggleSwitch from "../components/ToggleSwitch";
import { ReactComponent as BackIcon } from "../assets/icon_back.svg";
import {
  isMultiTrackingEnabled,
  setMultiTrackingEnabled,
} from "../utils/multiTrackingSetting";
import {
  isNotesSystemEnabled,
  setNotesSystemEnabled,
} from "../utils/notesSystemSetting";
import {
  getTitleDisplayMode,
  setTitleDisplayMode,
  TITLE_MODE_OPTIONS,
} from "../utils/titleDisplaySetting";
import {
  isShowLabelsOnTasks,
  setShowLabelsOnTasks,
} from "../utils/showLabelsSetting";
import {
  getHourlyRate,
  setHourlyRate,
  getAiHourlyRate,
  setAiHourlyRate,
  isAiInvoiceEnabled,
  setAiInvoiceEnabled,
  getCurrency,
  setCurrency,
  CURRENCY_CODES,
} from "../utils/billingRateSetting";
import {
  getStatCardsDisplayMode,
  setStatCardsDisplayMode,
  STAT_DISPLAY_MODES,
} from "../utils/statCardsSetting";

// Thin wrapper so existing call sites keep their zero-arg onChange handlers
// (which read + flip the current state) while rendering the unified pill toggle.
const Toggle = ({ checked, onChange }) => (
  <ToggleSwitch checked={checked} onChange={() => onChange()} />
);

const SettingsScreen = () => {
  const navigate = useNavigate();
  const { enforceSingleTracking } = useContext(FirebaseContext);
  const { theme, toggleTheme } = useTheme();
  const [multiTracking, setMultiTracking] = useState(isMultiTrackingEnabled());
  const [notesSystem, setNotesSystem] = useState(isNotesSystemEnabled());
  const [titleMode, setTitleMode] = useState(getTitleDisplayMode());
  const [showLabels, setShowLabels] = useState(isShowLabelsOnTasks());
  const [currency, setCurrencyStateLocal] = useState(getCurrency());
  const [hourlyRate, setHourlyRateState] = useState(String(getHourlyRate()));
  const [aiRate, setAiRateState] = useState(String(getAiHourlyRate()));
  const [aiInvoice, setAiInvoice] = useState(isAiInvoiceEnabled());
  const [statDisplayMode, setStatDisplayModeState] = useState(
    getStatCardsDisplayMode()
  );

  const handleChangeStatDisplayMode = (e) => {
    const v = e.target.value;
    setStatDisplayModeState(v);
    setStatCardsDisplayMode(v);
  };

  const handleCurrencyChange = (e) => {
    const v = e.target.value;
    setCurrency(v);
    setCurrencyStateLocal(v);
    // Rates fall back to the new currency's defaults when unset — reflect that.
    setHourlyRateState(String(getHourlyRate()));
    setAiRateState(String(getAiHourlyRate()));
  };

  const handleRateChange = (e) => {
    const v = e.target.value;
    setHourlyRateState(v);
    setHourlyRate(v); // ignores invalid/negative; keeps prior stored value
  };

  const handleAiRateChange = (e) => {
    const v = e.target.value;
    setAiRateState(v);
    setAiHourlyRate(v);
  };

  const handleToggleAiInvoice = () => {
    const next = !aiInvoice;
    setAiInvoice(next);
    setAiInvoiceEnabled(next);
  };

  const handleChangeTitleMode = (e) => {
    setTitleMode(e.target.value);
    setTitleDisplayMode(e.target.value); // localStorage only
  };

  const handleToggleShowLabels = () => {
    const next = !showLabels;
    setShowLabels(next);
    setShowLabelsOnTasks(next); // localStorage only
  };

  const handleToggleMultiTracking = () => {
    const next = !multiTracking;
    setMultiTracking(next);
    setMultiTrackingEnabled(next); // localStorage only — not persisted to Firebase yet
    if (!next) {
      // Turning multi-tracking off: keep the latest timer, stop the rest.
      enforceSingleTracking();
    }
  };

  const handleToggleNotesSystem = () => {
    const next = !notesSystem;
    setNotesSystem(next);
    setNotesSystemEnabled(next); // localStorage only
  };

  return (
    <div style={{ paddingTop: "80px" }}>
      <TopBar
        title="Settings"
        leftContainer={
          <button
            onClick={() => navigate(-1)}
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <BackIcon
              style={{
                width: "24px",
                height: "24px",
                filter: "var(--icon-filter)",
              }}
            />
          </button>
        }
      />
      <div style={styles.body}>
        <h3 style={styles.sectionHeader}>Appearance</h3>
        <div style={styles.row}>
          <div style={styles.textCol}>
            <div style={styles.title}>Dark theme</div>
            <div style={styles.description}>
              Switch between dark and light appearance.
            </div>
          </div>
          <Toggle checked={theme === "dark"} onChange={toggleTheme} />
        </div>

        <h3 style={styles.sectionHeader}>Display</h3>
        <div style={styles.row}>
          <div style={styles.textCol}>
            <div style={styles.title}>Notes System</div>
            <div style={styles.description}>
              Take notes per project, shown in a Notes tab.
            </div>
          </div>
          <Toggle checked={notesSystem} onChange={handleToggleNotesSystem} />
        </div>
        <div style={styles.row}>
          <div style={styles.textCol}>
            <div style={styles.title}>Show Labels on Tasks</div>
            <div style={styles.description}>
              Display label chips on each task card.
            </div>
          </div>
          <Toggle checked={showLabels} onChange={handleToggleShowLabels} />
        </div>
        <div style={styles.row}>
          <div style={styles.textCol}>
            <div style={styles.title}>Task Title Display Mode</div>
            <div style={styles.description}>
              Scrolling marquee, single line with ellipsis, or wrap across
              multiple lines.
            </div>
          </div>
          <select
            value={titleMode}
            onChange={handleChangeTitleMode}
            style={styles.select}
          >
            {TITLE_MODE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div style={styles.row}>
          <div style={styles.textCol}>
            <div style={styles.title}>Stat Cards Display Mode</div>
            <div style={styles.description}>
              Show Non Empty hides cards whose value is still zero (and the whole
              row when nothing has happened yet), Always Show renders every card,
              or Hide skips the row entirely.
            </div>
          </div>
          <select
            value={statDisplayMode}
            onChange={handleChangeStatDisplayMode}
            style={styles.select}
          >
            {STAT_DISPLAY_MODES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <h3 style={styles.sectionHeader}>Tracking</h3>
        <div style={styles.row}>
          <div style={styles.textCol}>
            <div style={styles.title}>Multi Tracking System</div>
            <div style={styles.description}>Track up to 5 tasks at once.</div>
          </div>
          <Toggle checked={multiTracking} onChange={handleToggleMultiTracking} />
        </div>

        <h3 style={styles.sectionHeader}>Billing</h3>
        <div style={styles.row}>
          <div style={styles.textCol}>
            <div style={styles.title}>Currency</div>
            <div style={styles.description}>
              Currency for the Billing stat card. Each has its own default
              rates.
            </div>
          </div>
          <select
            value={currency}
            onChange={handleCurrencyChange}
            style={styles.select}
          >
            {CURRENCY_CODES.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </div>
        <div style={styles.row}>
          <div style={styles.textCol}>
            <div style={styles.title}>Hourly Rate</div>
            <div style={styles.description}>
              Human billing rate. Human-tracked hours × this rate.
            </div>
          </div>
          <input
            type="number"
            min="0"
            step="0.5"
            value={hourlyRate}
            onChange={handleRateChange}
            style={{ ...styles.select, width: "90px" }}
          />
        </div>
        <div style={styles.row}>
          <div style={styles.textCol}>
            <div style={styles.title}>AI Invoice</div>
            <div style={styles.description}>
              If you need to track the AI spend and separate the billing.
            </div>
          </div>
          <Toggle checked={aiInvoice} onChange={handleToggleAiInvoice} />
        </div>
        {aiInvoice && (
          <div style={styles.row}>
            <div style={styles.textCol}>
              <div style={styles.title}>AI Hourly Rate</div>
              <div style={styles.description}>
                Rate applied to AI-tracked hours.
              </div>
            </div>
            <input
              type="number"
              min="0"
              step="0.5"
              value={aiRate}
              onChange={handleAiRateChange}
              style={{ ...styles.select, width: "90px" }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  body: {
    padding: "16px",
  },
  sectionHeader: {
    color: "var(--text-faint)",
    fontSize: "13px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    margin: "8px 0 12px",
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    padding: "12px 0",
    borderBottom: "1px solid var(--border)",
  },
  textCol: {
    flex: 1,
  },
  title: {
    color: "var(--text)",
    fontSize: "16px",
    fontWeight: "bold",
  },
  description: {
    color: "var(--text-muted)",
    fontSize: "14px",
    marginTop: "4px",
  },
  switch: {
    position: "relative",
    width: "46px",
    height: "26px",
    borderRadius: "13px",
    border: "none",
    cursor: "pointer",
    flexShrink: 0,
    padding: "2px",
    transition: "background-color 0.15s",
  },
  knob: {
    display: "block",
    width: "22px",
    height: "22px",
    borderRadius: "50%",
    backgroundColor: "#fff",
    transition: "transform 0.15s",
  },
  select: {
    flexShrink: 0,
    backgroundColor: "var(--surface)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: "8px",
    padding: "8px 10px",
    fontSize: "14px",
    cursor: "pointer",
  },
};

export default SettingsScreen;
